(function () {
  'use strict';

  /* ════════════════════════════════════════════
     ⚙️  GIFT TIER CONFIG
     ════════════════════════════════════════════ */
  const GIFT_TIERS = [
    { handle: 'designer-sling-hanger',  threshold: 499, variantId: null },
    { handle: 'purple-planter', threshold: 2000, variantId: null }
  ];

  let giftVariantsResolved = false;
  let isProcessingGifts = false;

  /* ── Resolve variant IDs from product handles (runs once) ── */
  async function resolveVariantIds() {
    if (giftVariantsResolved) return;
    const promises = GIFT_TIERS.map(async (tier) => {
      try {
        const res = await fetch('/products/' + tier.handle + '.js');
        if (!res.ok) throw new Error('Not found: ' + tier.handle);
        const data = await res.json();
        tier.variantId = data.variants[0].id;
        console.log('[Sage Gift] Resolved', tier.handle, '→', tier.variantId);
      } catch (err) {
        console.error('[Sage Gift] Failed:', tier.handle, err);
      }
    });
    await Promise.all(promises);
    giftVariantsResolved = true;
  }

  /* ── Cart helpers ── */
  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  }

  function getGiftVariantIds() {
    return GIFT_TIERS.map(t => t.variantId).filter(Boolean);
  }

  function cartTotalWithoutGifts(cart) {
    const giftIds = getGiftVariantIds();
    let total = 0;
    cart.items.forEach(item => {
      if (!giftIds.includes(item.variant_id)) {
        total += item.final_line_price;
      }
    });
    return total / 100; // paise → rupees
  }

  function isInCart(cart, variantId) {
    return cart.items.some(item => item.variant_id === variantId);
  }

  async function addToCart(variantId) {
    await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { '_auto_gift': 'true' } }]
      })
    });
  }

  async function removeFromCart(variantId) {
    await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId.toString(), quantity: 0 })
    });
  }

  /* ── Main gift evaluation ── */
  async function evaluateGifts() {
    if (isProcessingGifts || !giftVariantsResolved) return;
    isProcessingGifts = true;

    try {
      let cart = await getCart();
      let changed = false;
      const total = cartTotalWithoutGifts(cart);

      for (const tier of GIFT_TIERS) {
        if (!tier.variantId) continue;
        const inCart = isInCart(cart, tier.variantId);

        if (total >= tier.threshold && !inCart) {
          await addToCart(tier.variantId);
          changed = true;
          console.log('[Sage Gift] ✅ Added', tier.handle, '— total: ₹' + total);
        } else if (total < tier.threshold && inCart) {
          await removeFromCart(tier.variantId);
          changed = true;
          console.log('[Sage Gift] ❌ Removed', tier.handle, '— total: ₹' + total);
        }
      }

      if (changed) {
        // Trigger the theme's own cart drawer refresh so UI updates
        document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
      }
    } catch (err) {
      console.error('[Sage Gift] Error:', err);
    } finally {
      isProcessingGifts = false;
    }
  }

  /* ── Intercept all cart AJAX calls to auto-evaluate ── */
  function interceptCartChanges() {
    const origFetch = window.fetch;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      return origFetch.apply(this, args).then(response => {
        if (/\/cart\/(add|change|update|clear)/.test(url)) {
          // Skip if this was our own gift call
          const body = args[1]?.body;
          if (body && typeof body === 'string' && body.includes('_auto_gift')) {
            return response;
          }
          // Wait for Shopify to process, then evaluate
          setTimeout(() => evaluateGifts(), 600);
        }
        return response;
      });
    };

    // Also intercept XMLHttpRequest for older add-to-cart forms
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._sageUrl = url;
      return origOpen.apply(this, arguments);
    };
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (body) {
      this.addEventListener('load', function () {
        if (this._sageUrl && /\/cart\/(add|change|update|clear)/.test(this._sageUrl)) {
          if (body && typeof body === 'string' && body.includes('_auto_gift')) return;
          setTimeout(() => evaluateGifts(), 600);
        }
      });
      return origSend.apply(this, arguments);
    };
  }

  /* ════════════════════════════════════════════
     EXISTING: Lock progress bar after first animation
     ════════════════════════════════════════════ */
  function lockProgressBar() {
    document.querySelectorAll('.sage-tier__seg-fill').forEach(el => {
      if (el._locked) return;
      el._locked = true;
      el.addEventListener('animationend', function () {
        const fill = el.style.getPropertyValue('--sage-fill');
        el.style.animation = 'none';
        el.style.transition = 'width 0.6s ease';
        el.style.width = fill;
      }, { once: true });
    });
  }

  /* ════════════════════════════════════════════
     EXISTING: Upsell button (event delegation)
     ════════════════════════════════════════════ */
  document.addEventListener('click', async function (e) {
    const btn = e.target.closest('[data-upsell-add]');
    if (!btn) return;
    if (btn.disabled) return;

    e.preventDefault();
    btn.disabled = true;
    btn.textContent = '...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: btn.dataset.variantId, quantity: 1 }),
      });
      const data = await res.json();
      if (data.id || data.items) {
        btn.textContent = '✓';
        btn.style.background = '#528338';
        setTimeout(() => {
          const s = document.getElementById('sage-upsell-section');
          if (s) s.style.display = 'none';
          document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
        }, 600);
      } else {
        btn.disabled = false;
        btn.textContent = '+';
      }
    } catch (e) {
      btn.disabled = false;
      btn.textContent = '+';
    }
  });

  /* ════════════════════════════════════════════
     INIT
     ════════════════════════════════════════════ */
  async function init() {
    await resolveVariantIds();
    interceptCartChanges();
    // Evaluate on first load (in case page loads with items already in cart)
    evaluateGifts();
    console.log('[Sage Gift] Initialized — tiers:', GIFT_TIERS.map(t => t.handle + ' @₹' + t.threshold).join(', '));
  }

  document.addEventListener('DOMContentLoaded', () => {
    lockProgressBar();
    init();
  });
  document.addEventListener('cart-drawer:refresh', () => setTimeout(lockProgressBar, 400));

})();
